import React from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { FeedbackDailyCount } from "@/interfaces/chat-metrics.interface";
import { cn } from "@/lib/utils";

interface ChatMetricsChartProps {
  data: FeedbackDailyCount[];
  className?: string;
}

const POSITIVE_COLOR = "#799A3E";
const NEGATIVE_COLOR = "#DC3545";
const QUESTIONS_COLOR = "#005587";

// Parse "YYYY-MM-DD" as noon UTC so it falls on the correct calendar day in Eastern time
const toTimestamp = (dateStr: string): number => new Date(dateStr + "T12:00:00Z").getTime();

const formatTick = (ts: number): string => {
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div style={{ fontSize: 14, color: "#171c26", border: "1px solid #E6E6E6", borderRadius: 4, background: "#fff", padding: "8px 10px" }}>
      <div style={{ marginBottom: 4, fontWeight: 600 }}>{new Date(label as number).toLocaleDateString()}</div>
      <div style={{ color: POSITIVE_COLOR }}>👍 Positive: {point.positive ?? 0}</div>
      <div style={{ color: NEGATIVE_COLOR }}>👎 Negative: {point.negative ?? 0}</div>
      <div style={{ color: QUESTIONS_COLOR, marginTop: 4 }}>Questions: {point.assistant_messages ?? 0}</div>
      <div style={{ color: "#555", marginTop: 2 }}>Unique Users: {point.unique_users ?? 0}</div>
    </div>
  );
};

const CustomLegend = ({ payload }: any) => {
  const feedback = payload?.filter((p: any) => p.value === "positive" || p.value === "negative") ?? [];
  const questions = payload?.find((p: any) => p.value === "assistant_messages");
  const feedbackLabel = (value: string) => value === "positive" ? "👍 Positive" : "👎 Negative";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 16, fontSize: 16 }}>
      <div style={{ display: "flex", gap: 16 }}>
        {feedback.map((entry: any) => (
          <span key={entry.value} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: entry.color }} />
            <span style={{ color: "#4a4a4a" }}>{feedbackLabel(entry.value)}</span>
          </span>
        ))}
      </div>
      {questions && (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-block", width: 11, height: 11, border: `2px solid ${QUESTIONS_COLOR}`, background: "rgba(0, 85, 135, 0.15)", borderRadius: 2 }} />
          <span style={{ color: "#4a4a4a" }}>Questions</span>
        </span>
      )}
    </div>
  );
};

export const ChatMetricsChart: React.FC<ChatMetricsChartProps> = ({ data, className }) => {
  if (!data || data.length === 0) return null;

  const chartData = data.map((d) => ({
    date: toTimestamp(d.date),
    positive: d.positive,
    negative: d.negative,
    assistant_messages: d.assistant_messages,
    unique_users: d.unique_users,
  }));

  const allTimestamps = chartData.map((d) => d.date);
  const minTs = Math.min(...allTimestamps);
  const maxTs = Math.max(...allTimestamps);

  return (
    <div className={cn("bg-background rounded-md border border-border p-4 chat-metrics-chart", className)}>
      <div className="w-full max-w-[1200px]">
        <ResponsiveContainer width="100%" height={375}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 60, left: 5, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E6E6" />
            <XAxis
              dataKey="date"
              type="number"
              scale="time"
              domain={[minTs, maxTs]}
              tickFormatter={formatTick}
              tick={{ fill: "#171c26", fontSize: 12, fontWeight: 500 }}
              stroke="#E6E6E6"
            />
            <YAxis
              yAxisId="left"
              allowDecimals={false}
              tick={{ fill: "#171c26", fontSize: 16 }}
              stroke="#E6E6E6"
              label={{ value: "Feedback", angle: -90, position: "insideLeft", offset: 10, style: { fontSize: 15, fill: "#555" } }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              allowDecimals={false}
              tick={{ fill: QUESTIONS_COLOR, fontSize: 16 }}
              stroke="#E6E6E6"
              label={{ value: "Questions", angle: 90, position: "insideRight", offset: 10, style: { fontSize: 15, fill: QUESTIONS_COLOR } }}
            />
            <Tooltip
              isAnimationActive={false}
              content={<CustomTooltip />}
            />
            <Legend content={<CustomLegend />} wrapperStyle={{ paddingLeft: 65, paddingRight: 60 }} />
            <Area
              type="monotone"
              dataKey="assistant_messages"
              yAxisId="right"
              legendType="circle"
              stroke={QUESTIONS_COLOR}
              fill="rgba(0, 85, 135, 0.12)"
              strokeWidth={1.5}
              dot={false}
              name="assistant_messages"
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="positive"
              yAxisId="left"
              legendType="circle"
              stroke={POSITIVE_COLOR}
              strokeOpacity={0.75}
              dot={{ fill: POSITIVE_COLOR, r: 4 }}
              activeDot={{ r: 6 }}
              strokeWidth={3.5}
              name="positive"
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="negative"
              yAxisId="left"
              legendType="circle"
              stroke={NEGATIVE_COLOR}
              strokeOpacity={0.75}
              dot={{ fill: NEGATIVE_COLOR, r: 4 }}
              activeDot={{ r: 6 }}
              strokeWidth={3.5}
              name="negative"
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
